import type { BetterAuthClientPlugin } from "better-auth/client";
import type { smartOnFhir } from ".";

/**
 * Client plugin for SMART on FHIR authentication. Add it to `createAuthClient`,
 * then call `authClient.signIn.smart({ fhirBaseUrl })` to sign in, which links
 * the account instead when the caller is already logged in, or
 * `authClient.link.smart({ fhirBaseUrl })` to link one explicitly.
 */
export const smartOnFhirClient = () => {
    return {
        id: "smart-on-fhir",
        $InferServerPlugin: {} as ReturnType<typeof smartOnFhir>,
        getActions: ($fetch) => ({
            link: {
                smart: async (params: {
                    fhirBaseUrl: string;
                    callbackURL?: string;
                    /** Required: the org this EHR is for — ensures correct org binding */
                    organizationId: string;
                }) => {
                    const response = await $fetch<{ url: string; redirect: boolean }>("/link/smart", {
                        method: "POST",
                        body: params,
                    });
                    // `$fetch` resolves with `{ data, error }` rather than
                    // throwing, so an unchecked error read as success: no
                    // redirect, no message, nothing on screen. Every reason the
                    // endpoint refuses is specific and worth showing, so it is
                    // raised for the caller to surface.
                    if (response.error) {
                        throw new Error(
                            response.error.message ||
                                response.error.statusText ||
                                "Could not start the connection",
                        );
                    }
                    if (!response.data?.url) {
                        throw new Error("The health system did not return a sign-in link");
                    }
                    window.location.href = response.data.url;
                    return response;
                },
            },
        }),
    } satisfies BetterAuthClientPlugin;
};
